/**
 * MarkdownPastePlugin
 *
 * Intercepte le PASTE_COMMAND pour convertir du texte markdown collé
 * en nœuds Lexical riches, insérés à la position du curseur.
 *
 * Pipeline:
 *   text/plain → markdownToHtml() → DOMParser → $generateNodesFromDOM → $insertGeneratedNodes
 *
 * Ne s'active que si :
 * - Le clipboard ne contient pas de text/html (cas : VS Code, éditeur markdown, terminal)
 * - Le text/plain ressemble à du markdown structuré
 *
 * Si text/html est présent (copie depuis navigateur/Word/Notion), on laisse
 * le RichTextPlugin gérer le coller normalement.
 */

import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_HIGH, PASTE_COMMAND, $getSelection, $isRangeSelection } from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';
import { $insertGeneratedNodes } from '@lexical/clipboard';

// ─── Markdown detection ───────────────────────────────────────────────────────

// Matches: headings, fenced code, lists, blockquotes, bold, inline code
const MD_PATTERN = /^#{1,6}\s|^```|^[-*+]\s|^\d+\.\s|^>\s|\*\*[\s\S]+?\*\*|__[\s\S]+?__|`[^`]/m;

// ─── HTML escaping ────────────────────────────────────────────────────────────

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ─── Inline markdown → HTML ───────────────────────────────────────────────────

function processInline(text: string): string {
  return (
    text
      // Bold (** and __)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
      // Italic (* and _) — after bold to avoid greedy conflict
      .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
      .replace(/_([^_]+?)_/g, '<em>$1</em>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Hyperlinks
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>')
  );
}

// ─── Block markdown → HTML ────────────────────────────────────────────────────

function markdownToHtml(md: string): string {
  const lines = md.split('\n');
  const out: string[] = [];
  let i = 0;
  let inCode = false;
  let codeLang = '';
  let codeLines: string[] = [];
  let inList = false;
  let listTag = '';

  const closeList = () => {
    if (inList) {
      out.push(`</${listTag}>`);
      inList = false;
      listTag = '';
    }
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // ── Fenced code block ─────────────────────────────────────────────────
    if (line.startsWith('```')) {
      if (inCode) {
        inCode = false;
        out.push(
          `<pre><code class="language-${esc(codeLang)}">${esc(codeLines.join('\n'))}</code></pre>`,
        );
        codeLines = [];
        codeLang = '';
      } else {
        closeList();
        inCode = true;
        codeLang = line.slice(3).trim();
      }
      i++;
      continue;
    }

    if (inCode) {
      codeLines.push(line);
      i++;
      continue;
    }

    // ── Checkbox list item (before unordered list) ────────────────────────
    const ckMatch = /^[-*+]\s+\[([ xX])\]\s+(.+)$/.exec(line);
    if (ckMatch) {
      if (!inList || listTag !== 'ul') {
        closeList();
        inList = true;
        listTag = 'ul';
        out.push('<ul>');
      }
      const checked = (ckMatch[1] ?? '').toLowerCase() === 'x' ? ' checked' : '';
      out.push(`<li><input type="checkbox"${checked} />${processInline(ckMatch[2] ?? '')}</li>`);
      i++;
      continue;
    }

    // ── Heading ───────────────────────────────────────────────────────────
    const hMatch = /^(#{1,3})\s+(.+)$/.exec(line);
    if (hMatch) {
      closeList();
      const lvl = (hMatch[1] ?? '').length;
      out.push(`<h${lvl}>${processInline(hMatch[2] ?? '')}</h${lvl}>`);
      i++;
      continue;
    }

    // ── Blockquote ────────────────────────────────────────────────────────
    const bqMatch = /^>\s*(.*)/.exec(line);
    if (bqMatch) {
      closeList();
      out.push(`<blockquote>${processInline(bqMatch[1] ?? '')}</blockquote>`);
      i++;
      continue;
    }

    // ── Horizontal rule ───────────────────────────────────────────────────
    if (/^[-*_]{3,}$/.test(line.trim())) {
      closeList();
      out.push('<hr />');
      i++;
      continue;
    }

    // ── Unordered list ────────────────────────────────────────────────────
    const ulMatch = /^[-*+]\s+(.+)$/.exec(line);
    if (ulMatch) {
      if (!inList || listTag !== 'ul') {
        closeList();
        inList = true;
        listTag = 'ul';
        out.push('<ul>');
      }
      out.push(`<li>${processInline(ulMatch[1] ?? '')}</li>`);
      i++;
      continue;
    }

    // ── Ordered list ──────────────────────────────────────────────────────
    const olMatch = /^\d+\.\s+(.+)$/.exec(line);
    if (olMatch) {
      if (!inList || listTag !== 'ol') {
        closeList();
        inList = true;
        listTag = 'ol';
        out.push('<ol>');
      }
      out.push(`<li>${processInline(olMatch[1] ?? '')}</li>`);
      i++;
      continue;
    }

    // ── Empty line ────────────────────────────────────────────────────────
    if (line.trim() === '') {
      closeList();
      i++;
      continue;
    }

    // ── Paragraph ─────────────────────────────────────────────────────────
    closeList();
    out.push(`<p>${processInline(line)}</p>`);
    i++;
  }

  // Close any unclosed block
  if (inCode) out.push(`<pre><code>${esc(codeLines.join('\n'))}</code></pre>`);
  closeList();

  return out.join('\n');
}

// ─── Plugin ───────────────────────────────────────────────────────────────────

export function MarkdownPastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const { clipboardData } = event;
        if (!clipboardData) return false;

        const text = clipboardData.getData('text/plain');
        if (!text) return false;

        // If clipboard already carries text/html (copy from browser/Word/Notion),
        // let RichTextPlugin handle it — it uses text/html with proper formatting.
        const html = clipboardData.getData('text/html');
        if (html && html !== text) return false;

        // Only intercept if the text looks like structured markdown
        if (!MD_PATTERN.test(text)) return false;

        event.preventDefault();

        const converted = markdownToHtml(text);
        const dom = new DOMParser().parseFromString(converted, 'text/html');

        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;
          const nodes = $generateNodesFromDOM(editor, dom);
          $insertGeneratedNodes(editor, nodes, selection);
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return null;
}
